import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/features/auth/useAuth'
import { useFeatureFlag } from '@/features/feature-flags/useFeatureFlag'
import { TOOLS, ToolIcon } from '@/lib/tools'
import './MobileBottomNav.css'

/**
 * Mobile-only bottom tab bar (≤900px). Four tabs:
 *   Home → /dashboard
 *   Tools → opens popover with the four TOOLS entries (Rhyme DB, RWG, Games, Training)
 *   +     → /documents/new
 *   Account → /settings?tab=account
 *
 * Hidden on desktop via CSS, hidden entirely when signed out (the landing /
 * login flow has its own chrome). Active-state highlighting comes from the
 * current pathname.
 */
export default function MobileBottomNav() {
  const { user } = useAuth()
  const { pathname } = useLocation()
  const [toolsOpen, setToolsOpen] = useState(false)
  const popoverRef = useRef(null)
  const toolsTabRef = useRef(null)

  // Feature flag values for Tools popover entries — read at the top level so
  // the filter below stays a pure render-time function.
  const flagValues = {
    'tools.rwg': useFeatureFlag('tools.rwg'),
    'dashboard.new-to-wexabet': useFeatureFlag('dashboard.new-to-wexabet'),
    'dashboard.training': useFeatureFlag('dashboard.training'),
  }
  const visibleTools = TOOLS.filter((t) => t.flag === null || flagValues[t.flag])

  // Close the popover on route change.
  useEffect(() => {
    setToolsOpen(false)
  }, [pathname])

  // Close the popover on outside click / Escape.
  useEffect(() => {
    if (!toolsOpen) return
    function onPointerDown(e) {
      if (popoverRef.current?.contains(e.target)) return
      if (toolsTabRef.current?.contains(e.target)) return
      setToolsOpen(false)
    }
    function onKey(e) {
      if (e.key === 'Escape') setToolsOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [toolsOpen])

  if (!user) return null

  const isHomeActive = pathname === '/dashboard' || pathname.startsWith('/documents')
  const isToolsActive = pathname.startsWith('/tools')
  const isAccountActive = pathname.startsWith('/settings')

  return (
    <nav className="mbn" aria-label="Primary mobile navigation">
      {toolsOpen && (
        <div className="mbn-popover" ref={popoverRef} role="menu" aria-label="Tools">
          {visibleTools.map((t) =>
            t.to ? (
              <Link
                key={t.name}
                to={t.to}
                className="mbn-popover-item"
                style={{ '--chip-color': t.color }}
                role="menuitem"
              >
                <span className="mbn-popover-icon" style={{ background: t.color }}>
                  <ToolIcon kind={t.icon} />
                </span>
                <span className="mbn-popover-label">{t.name}</span>
              </Link>
            ) : (
              <a
                key={t.name}
                href={t.href}
                className="mbn-popover-item"
                style={{ '--chip-color': t.color }}
                role="menuitem"
              >
                <span className="mbn-popover-icon" style={{ background: t.color }}>
                  <ToolIcon kind={t.icon} />
                </span>
                <span className="mbn-popover-label">{t.name}</span>
              </a>
            ),
          )}
        </div>
      )}

      <Link
        to="/dashboard"
        className="mbn-tab"
        aria-current={isHomeActive ? 'page' : undefined}
        data-active={isHomeActive || undefined}
      >
        <HomeIcon />
        <span className="mbn-tab-label">Home</span>
      </Link>

      <button
        type="button"
        ref={toolsTabRef}
        onClick={() => setToolsOpen((v) => !v)}
        className="mbn-tab"
        aria-haspopup="menu"
        aria-expanded={toolsOpen}
        data-active={isToolsActive || toolsOpen || undefined}
      >
        <ToolsIcon />
        <span className="mbn-tab-label">Tools</span>
      </button>

      <Link to="/documents/new" className="mbn-tab mbn-tab-cta" aria-label="New document">
        <PlusIcon />
        <span className="mbn-tab-label">New</span>
      </Link>

      <Link
        to="/settings?tab=account"
        className="mbn-tab"
        aria-current={isAccountActive ? 'page' : undefined}
        data-active={isAccountActive || undefined}
      >
        <UserIcon />
        <span className="mbn-tab-label">Account</span>
      </Link>
    </nav>
  )
}

// ── Icons (inline, no extra deps) ─────────────────────────────────────────

function HomeIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 9.5 12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z" />
    </svg>
  )
}

function ToolsIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  )
}

function UserIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  )
}
